<template>
  <div class="home-map-wrapper">
    <div class="home-map">
      <!-- 地图容器 -->
      <div id="map-container"></div>

      <!-- 区域描述面板（右下角） -->
      <div v-if="areaDescription" class="area-description-panel">
        <div class="panel-title">{{ areaDescription.title }}</div>
        <div class="panel-content">
          <p v-if="areaDescription.mileage"><strong>覆盖里程：</strong>{{ areaDescription.mileage }}</p>
          <p v-if="areaDescription.description" class="description-text">{{ areaDescription.description }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, shallowRef, defineProps, onMounted, onBeforeUnmount, watch, nextTick } from 'vue'
import AMapLoader from '@amap/amap-jsapi-loader'
import { getListByZoneId, getZoneInfo } from '@/api/test/testPath'

const props = defineProps({
  // 数据源ID
  dataSourceId: {
    type: String,
    default: ''
  }
})

// 地图实例引用
const map = shallowRef(null)
// 存储地图上的线、多边形、圆形等覆盖物
const polylines = ref([])

// 内部响应式变量存储地图数据
const mapData = ref([])
const areaDescription = ref(null)

// 初始化地图
const drawMap = async () => {
  try {
    // 等待DOM更新完成，确保容器尺寸已经确定
    await nextTick()

    const AMap = await AMapLoader.load({
      key: '6588d9e5b6ae0751c799cb4c7b3e30be', // 使用项目中已有的高德地图API Key
      version: '2.0',
      Loca: {
        version: '2.0.0'
      },
      plugins: ['AMap.MouseTool', 'AMap.PolylineEditor', 'AMap.PolyEditor']
    })

    // 获取地图容器的实际尺寸
    const mapContainer = document.getElementById('map-container')
    if (mapContainer) {
      const rect = mapContainer.getBoundingClientRect()
      console.log('地图容器尺寸:', rect.width, 'x', rect.height)
    }

    // 创建地图实例
    map.value = new AMap.Map('map-container', {
      zoom: 12,
      mapStyle: 'amap://styles/45311ae996a8bea0da10ad5151f72979', // 使用项目中已有的地图样式
      center: [103.726465, 36.107992] // 默认中心点（兰州）
    })

    map.value.setZooms([2, 30])

    // 添加地图加载完成事件
    map.value.on('complete', () => {
      console.log('地图加载完成')
      // 地图加载完成后添加路线
      if (mapData.value && mapData.value.length > 0) {
        addPolylines()
      }
    })

    // 监听窗口大小变化，自动调整地图尺寸
    const handleResize = () => {
      if (map.value) {
        map.value.resize()
      }
    }

    window.addEventListener('resize', handleResize)

    // 在组件销毁时移除事件监听
    onBeforeUnmount(() => {
      window.removeEventListener('resize', handleResize)
    })

  } catch (error) {
    console.error('地图加载失败:', error)
  }
}

// 添加路线和区域到地图
const addPolylines = () => {
  if (!map.value || !mapData.value) return

  // 清除现有覆盖物
  clearMap()

  polylines.value = []

  // 遍历地图数据，根据类型添加不同的覆盖物
  mapData.value.forEach(item => {
    if (item.locationInfo) {
      if (item.locationInfo.type === 'polygon') {
        // 绘制多边形区域
        polylines.value.push(addDrawPolygon(item))
      } else if (item.locationInfo.type === 'circle') {
        // 绘制圆形区域
        polylines.value.push(addDrawCircle(item))
      } else if (item.locationInfo.type === 'polyline' || item.locationInfo.coordinates) {
        // 绘制路线
        polylines.value.push(addDrawLines(item))
      }
    }
  })

  // 添加所有覆盖物到地图
  if (polylines.value.length > 0) {
    map.value.add(polylines.value)
    map.value.setFitView() // 调整地图视野，让所有覆盖物都可见
  }
}

// 绘制路线
const addDrawLines = (item) => {
  return new AMap.Polyline({
    path: item.locationInfo.coordinates,
    strokeColor: item.strokeColor || "#1E8EF2",
    strokeWeight: item.strokeWeight || 6,
    strokeOpacity: item.strokeOpacity || 0.9,
    zIndex: item.zIndex || 50,
    bubble: true
  })
}

// 绘制多边形区域
const addDrawPolygon = (item) => {
  return new AMap.Polygon({
    path: item.locationInfo.coordinates,
    zIndex: item.zIndex || 50,
    bubble: true,
    strokeColor: item.strokeColor || "#F23218",
    strokeOpacity: item.strokeOpacity || 0.7,
    strokeWeight: item.strokeWeight || 2,
    fillColor: item.fillColor || '#F2AA1E',
    fillOpacity: item.fillOpacity || 0.4,
    strokeStyle: item.strokeStyle || "solid"
  })
}

// 绘制圆形区域
const addDrawCircle = (item) => {
  return new AMap.Circle({
    center: item.locationInfo.coordinates,
    radius: item.locationInfo.radius || 1000, // 默认半径1000米
    borderWeight: 3,
    strokeColor: item.strokeColor || "#F23218",
    strokeOpacity: item.strokeOpacity || 0.7,
    strokeWeight: item.strokeWeight || 2,
    fillOpacity: item.fillOpacity || 0.4,
    strokeStyle: item.strokeStyle || 'solid',
    strokeDasharray: [10, 10],
    fillColor: item.fillColor || '#F2AA1E',
    zIndex: item.zIndex || 50
  })
}

// 清除地图上的所有覆盖物
const clearMap = () => {
  if (window.loca) {
    window.loca.clear()
  }
  if (polylines.value && polylines.value.length > 0 && map.value) {
    map.value.remove(polylines.value)
    polylines.value = []
  }
}

// 销毁地图
const destroyMap = () => {
  if (map.value) {
    map.value.destroy()
    map.value = null
    console.info("地图已销毁")
  }
}

const getZoneInfoData = async (zoneId) => {

  try {
    const res = await getZoneInfo(zoneId);
    areaDescription.value = {
      title: res.data.name,
      mileage: res.data.mileage + `公里`,
      description: res.data.description
    };
  }catch (error) {
    console.error('获取区域信息失败:', error);
  }
}

const getZoneData = async (zoneId) => {
  try {
    const res = await getListByZoneId(zoneId);

    // 处理返回的数据，解析locationInfo并格式化坐标
    const formattedData = res.data.map(item => {
      // 解析locationInfo JSON字符串
      let locationInfo;
      try {
        locationInfo = JSON.parse(item.locationInfo);
      } catch (error) {
        console.error('解析locationInfo失败:', error);
        locationInfo = {};
      }

      // 根据类型处理坐标数据
      if (locationInfo.type === 'polygon' && locationInfo.coordinates) {
        // 多边形数据格式处理：[[[lng,lat], [lng,lat], ...]] -> [[lng,lat], [lng,lat], ...]
        // 提取三维数组中的二维数组部分
        locationInfo.coordinates = locationInfo.coordinates[0] || [];
      } else if (locationInfo.type === 'circle' && locationInfo.coordinates) {
        // 圆形数据格式处理：确保radius是数字
        if (locationInfo.radius) {
          locationInfo.radius = parseFloat(locationInfo.radius);
        }
      }

      // 返回处理后的对象，保留原有的样式属性
      return {
        ...item,
        locationInfo,
        strokeColor: '#1E8EF2', // 默认线条颜色
        strokeWeight: 2, // 默认线条宽度
        strokeOpacity: 0.7, // 默认线条透明度
        fillColor: '#1EFFC0', // 默认填充颜色
        fillOpacity: 0.4, // 默认填充透明度
        zIndex: 50 // 默认层级
      };
    });

    mapData.value = formattedData;

    console.log("mapData", mapData.value);

    // 确保在数据更新后立即尝试渲染
    if (map.value) {
      console.log('数据更新后立即渲染地图');
      await nextTick();
      addPolylines();
    }

  } catch (error) {
    console.error('获取区域数据失败:', error);
  }
}

// 监听数据源ID变化
watch(() => props.dataSourceId, async (newVal) => {
  if (newVal) {
    console.log('数据源ID已更新:', newVal)
    await getZoneData(newVal);
    await getZoneInfoData(newVal);
    //await fetchMapData(newVal)
  }
}, { immediate: true }) // 立即执行一次

// 生命周期钩子
onMounted(async () => {
  await drawMap()
  // 如果地图数据已经加载（可能在地图初始化前），添加路线
  if (map.value && mapData.value && mapData.value.length > 0) {
    addPolylines()
  }
})

onBeforeUnmount(() => {
  destroyMap()
})
</script>

<style lang="less" scoped>
.home-map-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.home-map {
  position: relative;
  width: 100%;
  height: 100%;
  flex: 1;
  min-height: 0;
}

#map-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

/* 区域描述面板样式 */
.area-description-panel {
  position: absolute;
  bottom: 20px;
  right: 20px;
  width: 280px;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 8px;
  padding: 15px;
  color: #333333;
  z-index: 100;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(0, 0, 0, 0.1);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.panel-title {
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 10px;
  color: #1890ff;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  padding-bottom: 8px;
}

.panel-content {
  font-size: 14px;
  line-height: 1.6;
  color: #333333;
}

.panel-content p {
  margin: 5px 0;
  color: #333333;
}

.description-text {
  margin-top: 10px;
  opacity: 0.8;
  color: #333333;
}

/* 隐藏高德地图logo和版权信息 */
/deep/ .amap-logo {
  display: none !important;
}

/deep/ .amap-copyright {
  opacity: 0 !important;
}
</style>